import * as React from "react";
import { format } from "date-fns";
import type { Locale } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ClosingDatePickerContent } from "@/components/crm/ClosingDatePickerContent";
import { cn } from "@/lib/utils";
import { getDateFnsLocale } from "@/lib/date-fns-locale";

export interface DatePickerPopoverProps {
  /** ISO date string `yyyy-MM-dd` (or empty string for "no date"). */
  value: string;
  onChange: (value: string) => void;
  /** Override placeholder shown when value is empty. */
  placeholder?: string;
  /** Display format for the trigger button. Defaults to `dd.MM.yyyy`. */
  displayFormat?: string;
  /** date-fns locale. If omitted, derived from current i18n language. */
  locale?: Locale;
  disabled?: boolean;
  /** Show a small × to clear the date. Defaults to `true`. */
  clearable?: boolean;
  className?: string;
  /** Underlying button id (so a `<Label htmlFor>` lights up). */
  id?: string;
  ariaLabel?: string;
  /** Popover alignment. Defaults to `start`. */
  align?: "start" | "center" | "end";
  /** Popover side. Defaults to `bottom`. */
  side?: "top" | "right" | "bottom" | "left";
  /** Optional input-like styling (`w-full`). Defaults to `true`. */
  fullWidth?: boolean;
}

/**
 * Unified date picker for forward-looking date inputs (closing dates,
 * installation dates, scheduled visits, etc.). Renders a button-styled trigger
 * and a popover with the closing-date preset column + calendar. Selections and
 * preset clicks apply immediately via `onChange` and close the popover.
 */
export function DatePickerPopover({
  value,
  onChange,
  placeholder,
  displayFormat = "dd.MM.yyyy",
  locale,
  disabled = false,
  clearable = true,
  className,
  id,
  ariaLabel,
  align = "start",
  side = "bottom",
  fullWidth = true,
}: DatePickerPopoverProps) {
  const { t, i18n } = useTranslation();
  const resolvedLocale = locale ?? getDateFnsLocale(i18n.language);
  const [open, setOpen] = React.useState(false);

  const displayText = value
    ? format(new Date(value + "T00:00:00"), displayFormat, { locale: resolvedLocale })
    : placeholder ?? t("common.selectDate", "—");

  return (
    <Popover modal={false} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            "justify-start text-left font-normal",
            fullWidth && "w-full",
            !value && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0 opacity-70" />
          <span className="flex-1 truncate">{displayText}</span>
          {clearable && value && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              aria-label={t("common.cancel", "Clear")}
              className="ml-2 flex h-4 w-4 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange("");
              }}
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[100] w-auto p-0"
        align={align}
        side={side}
        collisionPadding={16}
      >
        <ClosingDatePickerContent
          value={value}
          onChange={(v) => {
            onChange(v);
            setOpen(false);
          }}
          locale={resolvedLocale}
          showFooter={false}
        />
      </PopoverContent>
    </Popover>
  );
}

export default DatePickerPopover;
